import React, {useContext} from 'react';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Skeleton from '@material-ui/lab/Skeleton';
import Box from '@material-ui/core/Box';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ArrowRightAltIcon from '@material-ui/icons/ArrowRightAlt';
import useGetPayoutOptions from '../../hooks/useGetPayoutOptions';
import {useGetMessages} from '../../../../hooks';
import {useFormContext} from 'react-hook-form';
import {RadioGroup} from '../../../../components/v2';
import {HeaderSkeleton, Link} from '../../../../components';
import {PayoutSetupInfo, PayoutTypes} from '../../../../../shared/types';
import {dateFormatUTC} from '../../../../../shared/utils/DateUtils';
import {CloseAccountContext} from '../..';
import useStyles, {Grid, GridItem, FormControlLabel} from './styles';

export interface PayoutSelectionProps {
    amount?: number;
    payoutSetupInfo: PayoutSetupInfo;
}

export interface PayoutSelectionFieldValues {
    payoutType: PayoutTypes;
}

export const PayoutSelection = React.memo<PayoutSelectionProps>(({payoutSetupInfo}) => {
    const {isRendering, payoutTypeSelected, amount = 0, formattedAmount} = useContext(
        CloseAccountContext
    );

    const styles = useStyles();
    const methods = useFormContext<PayoutSelectionFieldValues>();
    const options = useGetPayoutOptions(payoutSetupInfo, amount);
    const messages = useGetMessages(
        {
            header: 'epic.affiliate.closeAccount.selection.header.v2',
            required: 'errors.com.epicgames.validation.required_field.v2',
            cashWarning: {
                code: 'epic.affiliate.closeAccount.selection.payout.warning.v2',
                args: [<strong key="amount">{formattedAmount}</strong>, dateFormatUTC(Date.now())]
            }
        },
        [amount]
    );

    return (
        <RadioGroup
            row
            required={!isRendering}
            className={styles.radio}
            name="payoutType"
            control={methods.control}
            rules={{required: {value: true, message: messages.required}}}
            header={
                isRendering ? (
                    <HeaderSkeleton variant="h5" height={30} width={250} />
                ) : (
                    <Typography variant="h5">{messages.header}</Typography>
                )
            }>
            <Grid>
                {options.map(({listItems, ...option}, index) => {
                    if (isRendering) {
                        return (
                            <GridItem key={index}>
                                <Skeleton variant="rect" height={75} style={{borderRadius: 4}} />
                                <Box sx={{marginTop: 15}}>
                                    <Skeleton />
                                    <Skeleton />
                                    <Skeleton />
                                </Box>
                            </GridItem>
                        );
                    }

                    return (
                        <GridItem key={index}>
                            <Paper className={styles.paper}>
                                <FormControlLabel {...option} />
                            </Paper>
                            <List dense className={styles.list}>
                                {listItems.map(({icon, text, action}, index) => {
                                    return (
                                        <ListItem key={index} classes={{root: styles.listItem}}>
                                            <ListItemIcon>{icon}</ListItemIcon>
                                            <ListItemText
                                                secondary={text}
                                                classes={{root: styles.listItemText}}
                                            />
                                            {action && (
                                                <ListItemSecondaryAction
                                                    classes={{root: styles.listItemAction}}>
                                                    <Link
                                                        variant="caption"
                                                        to="settings"
                                                        color="primary"
                                                        underline="hover"
                                                        aria-label="link">
                                                        <span className={styles.linkText}>
                                                            {action}
                                                        </span>
                                                        <span className={styles.linkIcon}>
                                                            <ArrowRightAltIcon />
                                                        </span>
                                                    </Link>
                                                </ListItemSecondaryAction>
                                            )}
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </GridItem>
                    );
                })}
                {payoutTypeSelected === PayoutTypes.CASH && (
                    <GridItem lg={12}>
                        <Typography className={styles.warning}>{messages.cashWarning}</Typography>
                    </GridItem>
                )}
            </Grid>
        </RadioGroup>
    );
});

export default PayoutSelection;
